/**
 * Jinda POS — Unified Browser Print Utility
 * 
 * Provides isolated, sandboxed iframe printing for all printable modules in the application.
 * Prevents dark mode leakage, eliminates global @media print / @page collisions,
 * and ensures that only the dedicated business document is sent to browser print preview / PDF.
 */

export type PrintDocumentType = 'a4-portrait' | 'a4-landscape' | 'thermal-80mm' | 'thermal-58mm' | 'barcode-label' | 'custom';

export interface PrintOptions {
    title?: string;
    documentType?: PrintDocumentType;
    orientation?: 'portrait' | 'landscape';
    customStyles?: string;
    customCss?: string;
    extraStylesheets?: string[];
    autoPrint?: boolean;
    onBeforePrint?: () => void;
    onAfterPrint?: () => void;
    pageMargin?: string;
    fontSize?: string;
    copyStyles?: boolean;
}

export type PrintDocumentOptions = PrintOptions;

const getBaseStylesForType = (type: PrintDocumentType, options?: PrintOptions): string => {
    switch (type) {
        case 'a4-portrait':
            return `
                @page {
                    size: A4 portrait;
                    margin: ${options?.pageMargin || '12mm 15mm'};
                }
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    background: #FFFFFF !important;
                    color: #1E293B !important;
                    font-family: 'Kodchasan', 'Prompt', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    font-size: ${options?.fontSize || '13px'};
                    line-height: 1.5;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    page-break-inside: auto;
                }
                thead {
                    display: table-header-group;
                }
                tbody tr {
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                th, td {
                    padding: 8px 10px;
                }
                .no-break,
                .po-totals-section,
                .po-signatures-section,
                .voucher-signatures,
                .voucher-table tr,
                .voucher-sig-box {
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                /* Expense Voucher specific print styling */
                .expense-voucher-print-area {
                    width: 100% !important;
                    max-width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #FFFFFF !important;
                    box-shadow: none !important;
                    border: none !important;
                }
                .expense-voucher-paper {
                    background: #FFFFFF !important;
                    color: #1E293B !important;
                    padding: 0 !important;
                    box-shadow: none !important;
                    border: none !important;
                }
                .voucher-header {
                    display: flex;
                    justify-content: space-between;
                    border-bottom: 2px solid #FF7A59;
                    padding-bottom: 16px;
                    margin-bottom: 20px;
                }
                .voucher-store-name {
                    font-size: 20px;
                    font-weight: 700;
                    color: #FF7A59;
                }
                .voucher-store-address {
                    font-size: 11px;
                    color: #64748B;
                    margin-top: 4px;
                    line-height: 1.4;
                }
                .voucher-title-box {
                    text-align: right;
                }
                .voucher-main-title {
                    font-size: 20px;
                    font-weight: 800;
                    color: #1E293B;
                }
                .voucher-doc-no {
                    font-size: 13px;
                    font-weight: 700;
                    color: #FF7A59;
                }
                .voucher-meta-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                    margin-bottom: 20px;
                    background: #F8FAFC;
                    padding: 12px 16px;
                    border-radius: 8px;
                    border: 1px solid #E2E8F0;
                    font-size: 13px;
                }
                .voucher-meta-item {
                    display: flex;
                    gap: 8px;
                }
                .voucher-meta-label {
                    font-weight: 600;
                    color: #64748B;
                    min-width: 100px;
                }
                .voucher-meta-value {
                    font-weight: 700;
                    color: #1E293B;
                }
                .voucher-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-bottom: 20px;
                }
                .voucher-table th {
                    background: #FFF3EE;
                    color: #FF7A59;
                    font-weight: 700;
                    font-size: 13px;
                    padding: 8px 12px;
                    text-align: left;
                    border: 1px solid #FFD8CC;
                }
                .voucher-table td {
                    padding: 10px 12px;
                    border: 1px solid #E2E8F0;
                    font-size: 13px;
                }
                .voucher-signatures {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 20px;
                    margin-top: 30px;
                    padding-top: 15px;
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                .voucher-sig-box {
                    text-align: center;
                    font-size: 12px;
                    color: #64748B;
                }
                .voucher-sig-line {
                    border-bottom: 1px dotted #94A3B8;
                    height: 45px;
                    margin-bottom: 8px;
                }
                /* Purchase Order specific print styling */
                .po-print-container {
                    width: 100% !important;
                    max-width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #FFFFFF !important;
                    color: #1E293B !important;
                    box-shadow: none !important;
                }
            `;
        case 'a4-landscape':
            return `
                @page {
                    size: A4 landscape;
                    margin: ${options?.pageMargin || '10mm 12mm'};
                }
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    background: #FFFFFF !important;
                    color: #1E293B !important;
                    font-family: 'Kodchasan', 'Prompt', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    font-size: ${options?.fontSize || '12px'};
                    line-height: 1.4;
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    page-break-inside: auto;
                }
                thead {
                    display: table-header-group;
                }
                tbody tr {
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
                th, td {
                    padding: 6px 8px;
                    border: 1px solid #CBD5E1;
                }
                th {
                    background: #F1F5F9;
                    font-weight: 700;
                    color: #334155;
                }
                .no-break {
                    page-break-inside: avoid;
                    break-inside: avoid;
                }
            `;
        case 'thermal-58mm':
            return `
                @page {
                    size: 58mm auto;
                    margin: 0;
                }
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                .no-print, .no-print * {
                    display: none !important;
                    height: 0 !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    font-family: 'Prompt', 'Courier New', Courier, monospace, sans-serif;
                    font-size: ${options?.fontSize || '11px'};
                    line-height: 1.3;
                    width: 56mm;
                    max-width: 56mm;
                }
                .barcode-preview-grid {
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    gap: 3mm !important;
                    padding: 0 !important;
                    margin: 0 !important;
                    width: 56mm !important;
                }
                .barcode-label-card {
                    width: 54mm !important;
                    max-width: 54mm !important;
                    padding: 3mm 2mm !important;
                    margin: 0 auto 3mm auto !important;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    border: 1px dashed #94A3B8 !important;
                    border-radius: 4px !important;
                    box-shadow: none !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    text-align: center !important;
                    justify-content: center !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                    box-sizing: border-box !important;
                }
                .barcode-label-store {
                    font-size: 11px !important;
                    font-weight: 800 !important;
                    text-align: center !important;
                    width: 100% !important;
                    display: block !important;
                    margin-bottom: 2px !important;
                }
                .barcode-label-name {
                    font-size: 13px !important;
                    font-weight: 800 !important;
                    text-align: center !important;
                    width: 100% !important;
                    display: block !important;
                    margin-bottom: 4px !important;
                    line-height: 1.3 !important;
                }
                .barcode-label-code {
                    font-size: 10px !important;
                    color: #64748B !important;
                    text-align: center !important;
                    width: 100% !important;
                    display: block !important;
                }
                .barcode-label-card * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
            `;
        case 'thermal-80mm':
            return `
                @page {
                    size: 80mm auto;
                    margin: 0;
                }
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    font-family: 'Courier New', Courier, monospace, 'Prompt', sans-serif;
                    font-size: ${options?.fontSize || '12px'};
                    line-height: 1.35;
                    width: 78mm;
                    max-width: 78mm;
                }
                .thermal-slip-container-80mm {
                    width: 78mm !important;
                    max-width: 78mm !important;
                    padding: 4mm 2mm !important;
                    margin: 0 !important;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    box-shadow: none !important;
                    border: none !important;
                }
                .slip-header {
                    text-align: center;
                    margin-bottom: 6px;
                }
                .slip-store-name {
                    font-size: 15px;
                    font-weight: 800;
                    letter-spacing: 0.5px;
                }
                .slip-sub {
                    font-size: 11px;
                    color: #333333;
                    margin-top: 2px;
                }
                .slip-divider-dashed {
                    border-bottom: 1px dashed #000000;
                    margin: 6px 0;
                }
                .slip-divider-solid {
                    border-bottom: 1px solid #000000;
                    margin: 4px 0;
                }
                .slip-meta {
                    font-size: 11px;
                    line-height: 1.5;
                }
                .slip-row {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 2px;
                }
                .slip-section-title {
                    font-weight: 800;
                    font-size: 11px;
                    margin: 4px 0 2px 0;
                }
                .slip-highlight {
                    font-size: 13px;
                    font-weight: 800;
                }
                .slip-items-header {
                    display: grid;
                    grid-template-columns: 1fr 45px 65px;
                    font-weight: 700;
                    font-size: 11px;
                    margin-bottom: 2px;
                }
                .slip-items-header span:nth-child(2) {
                    text-align: center;
                }
                .slip-items-header span:nth-child(3) {
                    text-align: right;
                }
                .slip-item-row {
                    margin-bottom: 4px;
                }
                .slip-item-name {
                    font-weight: 600;
                    font-size: 11px;
                }
                .slip-item-calc {
                    display: flex;
                    justify-content: space-between;
                    font-size: 11px;
                    color: #222222;
                    padding-left: 8px;
                }
                .slip-signature-box {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 8px;
                    margin-top: 8px;
                    text-align: center;
                    font-size: 10px;
                }
                .slip-footer {
                    text-align: center;
                    font-size: 10px;
                    color: #444444;
                    margin-top: 8px;
                    line-height: 1.4;
                }
            `;
        case 'barcode-label':
            return `
                @page {
                    size: auto;
                    margin: ${options?.pageMargin || '4mm'};
                }
                * {
                    box-sizing: border-box;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }
                .barcode-preview-container {
                    background: transparent !important;
                    box-shadow: none !important;
                    border: none !important;
                    padding: 0 !important;
                    margin: 0 !important;
                }
                .barcode-preview-grid {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    gap: 4mm !important;
                    justify-content: flex-start !important;
                }
                .barcode-sticker-card {
                    break-inside: avoid !important;
                    page-break-inside: avoid !important;
                    border: 1px dashed #CBD5E1 !important;
                    background: #FFFFFF !important;
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    box-sizing: border-box !important;
                    padding: 2mm !important;
                }
                .barcode-store-header {
                    font-size: 10px !important;
                    font-weight: 700 !important;
                    color: #000000 !important;
                }
                .barcode-product-title {
                    font-size: 11px !important;
                    font-weight: 600 !important;
                    color: #000000 !important;
                    text-align: center !important;
                }
                .barcode-price-tag {
                    font-size: 12px !important;
                    font-weight: 800 !important;
                    color: #000000 !important;
                }
                .barcode-svg-wrapper svg {
                    max-width: 100% !important;
                }
            `;
        case 'custom':
        default:
            return `
                @page {
                    size: ${options?.orientation ? `auto ${options.orientation}` : 'auto'};
                    margin: ${options?.pageMargin !== undefined ? options.pageMargin : '0'};
                }
                * {
                    box-sizing: border-box !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                }
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #FFFFFF !important;
                    color: #000000 !important;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Prompt', sans-serif;
                }
            `;
    }
};

/**
 * Sends a specific DOM element to Browser Print Preview via a sandboxed, isolated iframe.
 * 
 * @param contentElement The target HTMLElement to print (e.g. printRef.current)
 * @param options Configuration options including documentType, title, margins, customStyles
 */
export const printDocument = (contentElement: HTMLElement | null, options?: PrintOptions): void => {
    if (!contentElement) {
        window.print();
        return;
    }

    const docType = options?.documentType || 'a4-portrait';
    const title = options?.title || document.title || 'พิมพ์เอกสาร';

    // Create a hidden, isolated iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('title', `Print Frame - ${title}`);
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) {
        document.body.removeChild(iframe);
        window.print();
        return;
    }

    const baseStyles = getBaseStylesForType(docType, options);
    const customStyles = options?.customStyles || '';

    doc.open();
    doc.write(`
        <!DOCTYPE html>
        <html lang="th">
        <head>
            <title></title>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
                ${baseStyles}
                ${customStyles}
            </style>
        </head>
        <body>
            ${contentElement.outerHTML}
        </body>
        </html>
    `);
    doc.close();

    // Trigger printing once iframe is fully prepared
    setTimeout(() => {
        try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } catch (e) {
            console.error('Print iframe error:', e);
            window.print();
        } finally {
            setTimeout(() => {
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
            }, 1500);
        }
    }, 200);
};

export default printDocument;
